import React from "react";
import { extractHeadings } from "./headings";

const TOKEN =
  /(`[^`]+`|\[[^\]]+\]\([^)]+\)|\*\*[^*]+?\*\*(?!\*)|\*[^*\n]+?\*|https?:\/\/[^\s)]+)/g;

function Inline({ text }) {
  const source = String(text).replace(/\\~/g, "~").replace(/---/g, "—");
  const nodes = [];
  let last = 0;
  let match;
  const re = new RegExp(TOKEN.source, "g");
  while ((match = re.exec(source))) {
    if (match.index > last) nodes.push(source.slice(last, match.index));
    const token = match[0];
    if (token.startsWith("`")) {
      nodes.push(<code key={match.index}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith("[")) {
      const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (link) {
        const href = link[2];
        const external = /^https?:\/\//.test(href);
        nodes.push(
          <a
            key={match.index}
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
          >
            {link[1]}
          </a>
        );
      } else {
        nodes.push(token);
      }
    } else if (token.startsWith("**")) {
      nodes.push(<strong key={match.index}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("*")) {
      nodes.push(<em key={match.index}>{token.slice(1, -1)}</em>);
    } else {
      nodes.push(
        <a
          key={match.index}
          href={token}
          target="_blank"
          rel="noopener noreferrer"
        >
          {token}
        </a>
      );
    }
    last = match.index + token.length;
  }
  if (last < source.length) nodes.push(source.slice(last));
  return <>{nodes}</>;
}

function parseTable(lines, start) {
  const rows = [];
  let i = start;
  while (i < lines.length && lines[i].includes("|")) {
    const cells = lines[i]
      .replace(/^\s*\|/, "")
      .replace(/\|\s*$/, "")
      .split("|")
      .map((cell) => cell.trim());
    if (!cells.every((cell) => /^:?-{3,}:?$/.test(cell))) rows.push(cells);
    i += 1;
  }
  return { rows, next: i };
}

function parseList(lines, start) {
  const items = [];
  let i = start;
  const itemRe = /^(\s*)(?:[-*]|\d+\.|•)\s+(.*)$/;
  while (i < lines.length) {
    const match = lines[i].match(itemRe);
    if (!match) break;
    let text = match[2];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !itemRe.test(lines[i]) &&
      !/^#{1,4}\s/.test(lines[i]) &&
      !/^!\[/.test(lines[i])
    ) {
      text += ` ${lines[i].trim()}`;
      i += 1;
    }
    items.push(text);
  }
  const ordered = /^\s*\d+\./.test(lines[start]);
  return { items, ordered, next: i };
}

export default function Markdown({ source }) {
  const lines = String(source).replace(/\r\n/g, "\n").split("\n");
  const headings = extractHeadings(source);
  const blocks = [];
  let i = 0;
  let key = 0;
  let headingIdx = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }
    if (/^-{3,}\s*$/.test(line.trim())) {
      blocks.push(<hr key={key++} />);
      i += 1;
      continue;
    }
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      const Tag = `h${heading[1].length}`;
      const toc =
        heading[1].length === 2 || heading[1].length === 3
          ? headings[headingIdx++]
          : null;
      blocks.push(
        <Tag key={key++} id={toc?.id}>
          <Inline text={heading[2]} />
        </Tag>
      );
      i += 1;
      continue;
    }
    const image = line.match(/^!\[([^\]]*)\]\(([^)]+)\)\s*$/);
    if (image) {
      blocks.push(
        <figure key={key++}>
          <img src={image[2]} alt={image[1]} />
          {image[1] ? <figcaption>{image[1]}</figcaption> : null}
        </figure>
      );
      i += 1;
      continue;
    }
    if (
      line.includes("|") &&
      lines[i + 1] &&
      /^\s*\|?\s*:?-{3,}/.test(lines[i + 1])
    ) {
      const table = parseTable(lines, i);
      const [head, ...body] = table.rows;
      blocks.push(
        <div className="nfw-md-table" key={key++}>
          <table>
            {head && (
              <thead>
                <tr>
                  {head.map((cell, ci) => (
                    <th key={ci}>
                      <Inline text={cell} />
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci}>
                      <Inline text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      i = table.next;
      continue;
    }
    if (/^\s*(?:[-*]|\d+\.|•)\s+/.test(line)) {
      const list = parseList(lines, i);
      const Tag = list.ordered ? "ol" : "ul";
      blocks.push(
        <Tag key={key++}>
          {list.items.map((item, idx) => (
            <li key={idx}>
              <Inline text={item} />
            </li>
          ))}
        </Tag>
      );
      i = list.next;
      continue;
    }
    const para = [line];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^#{1,4}\s/.test(lines[i]) &&
      !/^!\[/.test(lines[i]) &&
      !/^-{3,}\s*$/.test(lines[i].trim()) &&
      !/^\s*(?:[-*]|\d+\.|•)\s+/.test(lines[i]) &&
      !(lines[i].includes("|") && /^\s*\|?\s*:?-{3,}/.test(lines[i + 1] || ""))
    ) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={key++}>
        <Inline text={para.join(" ")} />
      </p>
    );
  }

  return <div className="nfw-md">{blocks}</div>;
}
